import React, { useCallback, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { FormControlLabel, Stack, SxProps, Typography } from '@mui/material';
import { Theme } from '@mui/system';

import invariant from 'invariant';

import DeleteIcon from '@watershed/icons/components/Delete';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button, { ButtonProps } from './Button';

import { usePortalContext } from '../utils/PortalContext';
import { Dialog, DialogHeaderProps, DialogProps } from './Dialog';
import ErrorBox from './ErrorBox';
import { Checkbox } from './Form/Checkbox';
import { TextFieldNonFormik } from './Form/TextField';
import ProgressButton from './ProgressButton';

type DialogConfirmProps = React.ComponentProps<typeof DialogConfirm>;

export type OpenDialogConfirmProps = Omit<DialogConfirmProps, 'onClose'> &
  Partial<Pick<DialogConfirmProps, 'onClose'>>;

export function useDialogConfirm() {
  const { addPortal, removePortal } = usePortalContext();

  const openDialog = useCallback(
    (props: OpenDialogConfirmProps) =>
      new Promise<boolean>((resolve) => {
        const portalId = addPortal(
          <DialogConfirm
            {...props}
            onClose={async (isConfirmed) => {
              await props.onClose?.(isConfirmed);
              resolve(isConfirmed);
              removePortal(portalId);
            }}
          />
        );
      }),
    [addPortal, removePortal]
  );

  return openDialog;
}

export function getDialogConfirmDeleteProps({
  itemName,
}: {
  itemName: LocalizedString;
}) {
  return {
    title: `Delete ${itemName}`,
    message: `Are you sure you want to delete this ${itemName}?`,
    confirmText: 'Delete',
    confirmIcon: <DeleteIcon />,
  };
}

// TODO (bryan): Organize the "confirm" button props into a single component,
// rather than having so many?
export default function DialogConfirm({
  onClose: initialOnClose,
  title,
  titleVariant,
  subtitle,
  border,
  headerActions,
  message,
  confirmText,
  confirmIcon,
  confirmColor,
  cancelText,
  cancelColor,
  errorMessage,
  autoFocusConfirm = true,
  disableConfirm,
  hideCancel,
  messageSx,
  promptText,
  requireCheckbox,
  ...dialogProps
}: {
  onClose: (isConfirmed: boolean) => void | Promise<void>;
  title: DialogHeaderProps['title'];
  titleVariant?: DialogHeaderProps['titleVariant'];
  subtitle?: DialogHeaderProps['subtitle'];
  border?: DialogHeaderProps['border'];
  headerActions?: DialogHeaderProps['actions'];
  message?: React.ReactNode;
  confirmText?: ButtonProps['children'];
  confirmIcon?: ButtonProps['startIcon'];
  confirmingText?: ButtonProps['children'];
  confirmColor?: ButtonProps['color'];
  cancelText?: ButtonProps['children'];
  cancelColor?: ButtonProps['color'];
  errorMessage?: React.ReactNode;
  autoFocusConfirm?: boolean;
  disableConfirm?: boolean;
  hideCancel?: boolean;
  messageSx?: SxProps<Theme>;
  promptText?: LocalizedString;
  requireCheckbox?: React.ReactNode;
} & Pick<
  DialogProps,
  'maxWidth' | 'disableBackdropClick' | 'disableEscapeKeyDown'
>) {
  const { t } = useLingui();
  confirmText ??= t({
    message: 'OK',
    context: 'Button title to confirm an action in a dialog.',
  });
  cancelText ??= t({
    message: 'Cancel',
    context: 'Button title to cancel out of a dialog.',
  });

  const [isConfirming, setIsConfirming] = useState(false);
  const [inputPromptText, setInputPromptText] = useState<string | undefined>(
    undefined
  );
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const onClose = async (isConfirmed: boolean) => {
    setIsConfirming(isConfirmed);
    await initialOnClose(isConfirmed);
    setIsConfirming(false);
  };
  const isMessageString = typeof message === 'string';
  invariant(
    isMessageString || !messageSx,
    'messageSx is not supported when message is an element. Use messageSx with a string message only'
  );
  const BodyElem = isMessageString ? Stack : React.Fragment;
  const bodyElemProps = isMessageString
    ? {
        gap: 1,
      }
    : undefined;
  // TODO (bryan): I think this string-parsing is a lil too clever - can/should
  // we audit existing usage and just pass in a variant instead? I see how this
  // inference helps avoid nonsensical dialogs (e.g. destructive action with a
  // blue button), but it doesn't feel extensible (e.g. I wanted to add the word
  // "Replace" but didn't want to break other usage that may rely on the
  // existing inference.)
  const inferredConfirmColor =
    typeof confirmText === 'string' &&
    (confirmText.includes('Delete') ||
      confirmText.includes('Remove') ||
      confirmText.includes('Reset'))
      ? 'error'
      : 'primary';

  const isPromptTextInvalid = !!promptText && inputPromptText !== promptText;
  const isCheckboxInvalid = !!requireCheckbox && !isCheckboxChecked;
  const maybePromptTextTooltip = isPromptTextInvalid
    ? t`Please enter the text “${promptText}” to confirm.`
    : undefined;
  const maybeCheckboxTooltip = isCheckboxInvalid
    ? t`Please check the checkbox to confirm.`
    : undefined;
  const maybeConfirmTooltip = maybePromptTextTooltip ?? maybeCheckboxTooltip;

  // Prevents normalization of whitespace in the prompt text. This prevents
  // prompting the user to copy and paste incorrect text if the prompt
  // contains 2+ spaces in a row.
  const nonNormalizedSx = { whiteSpace: 'pre-wrap' };

  return (
    <Dialog
      maxWidth="sm"
      {...dialogProps}
      onClose={() => onClose(false)}
      header={{
        sx: nonNormalizedSx,
        title,
        titleVariant,
        subtitle,
        actions: headerActions,
        hideClose: true,
        border,
      }}
      actions={
        <>
          {!hideCancel && (
            <Button onClick={() => onClose(false)} color={cancelColor}>
              {cancelText}
            </Button>
          )}
          <ProgressButton
            data-testid={TestIds.DialogConfirmSubmitButton}
            onClick={() => onClose(true)}
            isInProgress={isConfirming}
            startIcon={confirmIcon}
            autoFocus={autoFocusConfirm}
            color={confirmColor ?? inferredConfirmColor}
            disabled={
              disableConfirm || isPromptTextInvalid || isCheckboxInvalid
            }
            tooltip={maybeConfirmTooltip}
          >
            {confirmText}
          </ProgressButton>
        </>
      }
      children={
        message || errorMessage || promptText || requireCheckbox ? (
          <BodyElem {...bodyElemProps}>
            {isMessageString ? (
              <Typography variant="body2" sx={messageSx}>
                {message}
              </Typography>
            ) : (
              message
            )}
            {promptText && (
              <TextFieldNonFormik
                sx={nonNormalizedSx}
                label={
                  <Trans context="Wraps a provided dialog prompt with instructions">
                    Enter “{promptText}” without the quotes to continue.
                  </Trans>
                }
                id="prompt"
                value={inputPromptText}
                onChange={(e) => setInputPromptText(e.target.value)}
              />
            )}
            {requireCheckbox && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCheckboxChecked}
                    onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                  />
                }
                label={requireCheckbox}
              />
            )}
            {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}
          </BodyElem>
        ) : undefined
      }
    />
  );
}
